import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/ClientPortal.js LostReceiptModal (actual form: ONE textarea
//     "What was this transaction for?" + a "Wholly business?" yes/no toggle.
//     NOT two separate questions for "what" and "why".)
//   - src/pages/ClientPortal.js handleUnmarkLost (Undo button only; editing
//     in place isn't directly possible -- undo then re-mark with new text)
//   - src/pages/RequestDetail.js openLostReceiptsPrintView (one combined
//     PDF per request listing all lost rows in a table; NOT one PDF per
//     lost transaction)
//   - The PDF is NOT included in the Download all zip; it's opened in a
//     new tab via browser print dialog. The summary.csv covers the lost
//     rows for the audit trail.
// Removed:
//   - "Form asks: (1) What was this expense for? (2) Why is the receipt
//     unavailable?" -- actually one combined field.
//   - "PDF contains: A note that the declaration was submitted by the
//     client electronically through Receipt Chaser" -- not in the actual
//     template.
//   - "Each declaration PDF" / "declaration PDFs are included in the zip"
//     -- there's ONE combined PDF per request, opened separately, not in
//     the zip.
//   - "Declaration link instead of a file on the request page" -- actually
//     shows the explanation inline in amber text.
//   - "Receipt Chaser shows you a count of lost receipts per request and
//     per client over time" -- per-request yes, per-client over time no.
//   - "Edit the declaration text" -- not directly; client must undo then
//     re-mark.
// Pass 2 added:
//   - "Wholly business?" yes/no toggle on the client form (real, missing
//     from old article)
//   - The button to generate the PDF lives on the bookkeeper's request page

export default {
  title:   'Handling lost receipts and the HMRC declaration',
  summary: "What happens when a client can't find a receipt, how to view the declaration, and what to do with it.",
  body: (
    <>
      <P>
        Sometimes a client genuinely can't find a receipt. The supplier
        didn't issue one, the email got deleted, the paper receipt went
        through the wash. Rather than skip the transaction or have your
        client make something up, Receipt Chaser records a written
        declaration of what the expense was and notes that no receipt is
        available.
      </P>

      <Callout variant="info" title="Why this matters">
        HMRC expects a record for every business expense. When the
        original receipt is genuinely missing, a written declaration
        explaining what the expense was and confirming the business
        purpose is the standard fallback. It doesn't replace a receipt
        for VAT reclaim purposes, but it does cover the record-keeping
        requirement for the expense itself.
      </Callout>

      <H>What the client sees</H>
      <P>
        On the upload page, each pending transaction has two options:
        upload a file, or mark it as lost. Marking it as lost opens a
        short form asking:
      </P>
      <UL>
        <Li>
          <strong>What was this transaction for?</strong> A plain-English
          description. The form's placeholder gives an example:{' '}
          <em>"Business lunch with client at Café Rouge to discuss the
          Q3 project. Receipt not retained."</em> This is the single
          most important field. Be specific.
        </Li>
        <Li>
          <strong>Was this expense wholly for business purposes?</strong>{' '}
          A yes/no toggle. "No, partially personal" is the right answer
          when there's a personal element to the expense (a meal where
          a partner was present, mixed-use online subscription, etc.).
        </Li>
      </UL>
      <P>
        Once submitted, the transaction is marked as lost on both your
        side and theirs, with the client's explanation visible inline.
      </P>

      <Screenshot
        src="/support-screenshots/07 - mark as lost.png"
        alt="The Mark receipt as lost modal showing the transaction name and amount at the top, a What was this transaction for textarea with an example placeholder, a Was this expense wholly for business purposes yes/no toggle with Yes selected, and Cancel / Confirm buttons."
        caption="The lost-receipt form the client fills in, with the description textarea and the wholly-business toggle."
      />

      <H>Where to find lost-receipt rows as a bookkeeper</H>
      <P>
        On the request page, lost-receipt transactions are highlighted
        in faint amber and show the client's explanation in amber text
        below the description, along with the "Wholly business" flag.
        You can also filter the table to lost-only using the{' '}
        <Code>Showing</Code> dropdown above the transaction list.
      </P>

      <Screenshot
        src="/support-screenshots/07 - lost receipt line.png"
        alt="A single transaction row in the bookkeeper's table marked as lost, with an amber tint, the Lost status icon, and the client's explanation in amber text below the description: 'Petrol, they didn't give me a receipt · Wholly business: Yes'."
        caption="A lost-marked row in the bookkeeper's transaction table, with the amber tint and the client's explanation shown inline."
      />

      <H>Generating the declaration PDF</H>
      <P>
        When at least one row in a request is marked as lost, a{' '}
        <Code>📄 Lost receipts (PDF)</Code> button appears at the top
        of the request page. Click it to open a print-ready document
        listing every lost receipt in this request in a single table,
        with:
      </P>
      <UL>
        <Li>The transaction date and original description</Li>
        <Li>The amount</Li>
        <Li>The client's explanation</Li>
        <Li>Whether the expense was wholly for business</Li>
        <Li>Your practice and request name in the header</Li>
        <Li>The date the document was generated</Li>
      </UL>
      <P>
        The page opens in a new tab with a <Code>📄 Save as PDF</Code>{' '}
        button at the top. Click it to bring up your browser's print
        dialog, then choose "Save as PDF" as the destination.
      </P>

      <Screenshot
        src="/support-screenshots/07 - lost receipt pdf.png"
        alt="A Lost Receipt Declaration document showing the client name, request title, and generation date in the header, an explanatory banner, and a table listing the lost transaction with date, description, amount, the client's explanation, and the wholly-business answer."
        caption="An example Lost Receipt Declaration document, showing the table of lost transactions with explanations."
      />

      <Callout variant="info" title="The PDF is generated on demand">
        The declaration document isn't generated automatically and isn't
        included in the "Download all" zip. It's something you produce
        when you need a single printable summary, typically for sharing
        with the client, attaching to their accounting records, or
        having on file in case of an HMRC enquiry.
      </Callout>

      <H>What's in the audit-trail zip</H>
      <P>
        The zip that <Code>Download all</Code> builds contains real
        receipt files only. Lost-receipt rows are not included as
        separate files. They do appear in the{' '}
        <Code>summary.csv</Code> inside the zip though, with the
        explanation and the wholly-business flag, so the audit trail
        is complete. See{' '}
        <Link to="/support/reviewing-completed-receipts">Reviewing and downloading completed receipts</Link>.
      </P>

      <Callout variant="warning" title="Important: lost receipts and VAT">
        A lost-receipt declaration covers the expense for record-keeping
        purposes, but it does <strong>not</strong> entitle you to
        reclaim the VAT on that transaction. If VAT recovery matters
        (the supplier was VAT-registered and the expense is reclaimable),
        the right next step is for the client to contact the supplier
        and request a duplicate receipt or VAT invoice. The declaration
        is for the expense record only, not the VAT claim.
      </Callout>

      <H>When to push back, when to accept</H>
      <P>
        A handful of lost receipts on a busy month is normal. A pattern
        of them, especially for the same supplier or category, is worth
        flagging to your client.
      </P>
      <P>
        Typical guidance: cash transactions and certain types of online
        payment (PayPal balance, in-app purchases) are genuinely harder
        to retrieve. Card payments and online orders almost always have
        a receipt somewhere: the supplier's account page, the original
        confirmation email, the merchant's app. These are worth a
        five-minute search before being declared lost.
      </P>

      <H>Can a client undo a lost-receipt declaration?</H>
      <P>
        Yes. While the request is open, the client can return to the
        upload page using their original link, find the lost-marked
        transaction, and click <Code>Undo</Code> below the explanation.
        The row reverts to pending and the upload option returns. If
        they need to change the explanation, they can undo and re-mark
        with new text.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/reviewing-completed-receipts">Reviewing and downloading completed receipts</Link>
        </Li>
        <Li>
          <Link to="/support/sending-your-first-request">Sending your first receipt request</Link>
        </Li>
      </UL>
    </>
  ),
}
